<template>
  <div>
    <b-card v-if="$can('manage', 'fnf-league')" title="Импорт" class="mb-1">
      <b-form-group label="Расписание" label-for="host-slots-text">
        <b-form-textarea
          id="host-slots-text"
          v-model="importText"
          rows="8"
          placeholder="13.09.2026 12:30 - Серега Ворона"
        />
      </b-form-group>

      <b-button variant="primary" :disabled="!importText || isImporting" @click="doImport">
        <b-spinner v-if="isImporting" small class="mr-50" />
        Импортировать
      </b-button>

      <div v-if="importResult" class="mt-2">
        <p class="mb-50">
          Создано: <strong>{{ importResult.created }}</strong>,
          пропущено: <strong>{{ importResult.skipped.length }}</strong>
        </p>

        <div v-if="importResult.skipped.length" class="table-responsive">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Строка</th>
                <th>Причина</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in importResult.skipped" :key="index">
                <td>{{ item.line }}</td>
                <td>{{ skipReasonLabel(item.reason) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-card>

    <b-overlay :show="isLoading">
      <b-card no-body>
        <b-card-body class="d-flex justify-content-between align-items-center flex-wrap" style="gap: 1rem">
          <h4 class="mb-0">Слоты</h4>
          <b-button variant="outline-primary" size="sm" @click="copyList">
            <feather-icon icon="CopyIcon" class="mr-50" />
            Скопировать список
          </b-button>
        </b-card-body>
        <b-card-body class="pt-0">
          <vue-good-table
            :rows="slots"
            :columns="columns"
            :pagination-options="{ enabled: true, perPage: 25, perPageDropdown: [25, 50, 100] }"
            :sort-options="{ enabled: false }"
          >
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'starts_at'">{{ formatSlotDateTime(props.row.starts_at) }}</span>

              <span v-else-if="props.column.field === 'host_name'">{{ props.row.host_name }}</span>

              <span v-else-if="props.column.field === 'status'">
                <b-badge v-if="props.row.league_game_id" variant="light-warning">
                  Занят — {{ props.row.game_name }}
                </b-badge>
                <b-badge v-else-if="!props.row.is_active" variant="light-secondary">
                  Неактивен
                </b-badge>
                <b-badge v-else variant="light-success">
                  Свободен
                </b-badge>
              </span>

              <span v-else-if="props.column.field === 'actions'">
                <b-button
                  v-if="$can('manage', 'fnf-league')"
                  size="sm"
                  variant="flat-danger"
                  :disabled="deletingId === props.row.id"
                  @click="removeSlot(props.row)"
                >
                  <b-spinner v-if="deletingId === props.row.id" small />
                  <feather-icon v-else icon="TrashIcon" />
                </b-button>
              </span>

              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BButton, BSpinner, BOverlay, BBadge, BFormGroup, BFormTextarea,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { ref, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import fnfLeagueStoreModule from '@/views/fnf-league/fnfLeagueStoreModule'

const STORE_MODULE_NAME = 'app-fnf-league'

const SKIP_REASON_LABELS = {
  invalid_format: 'Не удалось распарсить строку',
  unknown_host: 'Ведущий не найден в справочнике',
  duplicate: 'Такой слот уже есть',
}

// Родительный падеж, нижний регистр — для формата копируемого списка «N. 13 сентября 13:30»
const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
]

export default {
  components: {
    BCard, BCardBody, BButton, BSpinner, BOverlay, BBadge, BFormGroup, BFormTextarea, VueGoodTable,
  },
  setup(props, { root }) {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, fnfLeagueStoreModule)
    onUnmounted(() => { if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME) })

    const toast = useToast()
    const showError = title => toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'danger' } })
    const showSuccess = title => toast({ component: ToastificationContent, props: { title, icon: 'CheckIcon', variant: 'success' } })

    const isLoading = ref(false)
    const isImporting = ref(false)
    const deletingId = ref(null)
    const slots = ref([])
    const importText = ref('')
    const importResult = ref(null)

    const columns = [
      { label: 'Дата и время', field: 'starts_at' },
      { label: 'Ведущий', field: 'host_name' },
      { label: 'Статус', field: 'status' },
      { label: 'Действия', field: 'actions' },
    ]

    const skipReasonLabel = reason => SKIP_REASON_LABELS[reason] || reason

    // Бэкенд отдаёт starts_at уже в МСК (toIso8601String() при APP_TIMEZONE=Europe/Moscow),
    // строкой вида «2026-09-13T12:30:00+03:00». new Date(value) переводит время в часовой
    // пояс браузера — вместо этого берём компоненты даты/времени прямо из строки.
    const parseSlotDateTime = value => {
      const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || '')
      if (!match) return null
      const [, year, month, day, hours, minutes] = match

      return {
        year, month, day, hours, minutes,
      }
    }

    const formatSlotDateTime = value => {
      const parsed = parseSlotDateTime(value)
      if (!parsed) return '—'

      return `${parsed.day}.${parsed.month}.${parsed.year} ${parsed.hours}:${parsed.minutes}`
    }

    const formatSlotForCopy = value => {
      const parsed = parseSlotDateTime(value)
      if (!parsed) return ''

      return `${Number(parsed.day)} ${MONTHS_GENITIVE[Number(parsed.month) - 1]} ${parsed.hours}:${parsed.minutes}`
    }

    const loadSlots = () => {
      isLoading.value = true
      store.dispatch(`${STORE_MODULE_NAME}/fetchHostSlots`)
        .then(response => { slots.value = response.data.slots })
        .catch(() => showError('Ошибка загрузки слотов'))
        .finally(() => { isLoading.value = false })
    }

    const doImport = () => {
      isImporting.value = true
      store.dispatch(`${STORE_MODULE_NAME}/importHostSlots`, importText.value)
        .then(response => {
          importResult.value = response.data
          showSuccess(`Импортировано: создано ${response.data.created}, пропущено ${response.data.skipped.length}`)
          importText.value = ''
          loadSlots()
        })
        .catch(err => showError(err.response?.data?.message || 'Ошибка импорта расписания'))
        .finally(() => { isImporting.value = false })
    }

    const removeSlot = async row => {
      const confirmed = await root.$bvModal.msgBoxConfirm(
        `Удалить слот «${formatSlotDateTime(row.starts_at)} — ${row.host_name}»?`,
        { title: 'Удаление слота', okTitle: 'Удалить', cancelTitle: 'Отмена', okVariant: 'danger', centered: true },
      )
      if (!confirmed) return

      deletingId.value = row.id
      store.dispatch(`${STORE_MODULE_NAME}/deleteHostSlot`, row.id)
        .then(() => {
          showSuccess('Слот удалён')
          loadSlots()
        })
        .catch(() => showError('Ошибка удаления слота'))
        .finally(() => { deletingId.value = null })
    }

    const copyList = () => {
      // Неактивные (прошедшие незанятыми) слоты игрокам не предлагаются
      const freeSlots = slots.value.filter(slot => slot.league_game_id === null && slot.is_active)

      // Дедуп по дате-времени: разных ведущих со свободным слотом в одно и то же
      // время игроку различать незачем — список рассылается для выбора времени.
      const uniqueByStartsAt = new Map()
      freeSlots.forEach(slot => {
        if (!uniqueByStartsAt.has(slot.starts_at)) uniqueByStartsAt.set(slot.starts_at, slot)
      })

      // ISO-строка бэкенда сортируется лексикографически так же, как хронологически —
      // без new Date() и без риска сползти в часовой пояс браузера
      const sorted = [...uniqueByStartsAt.values()]
        .sort((a, b) => (a.starts_at || '').localeCompare(b.starts_at || ''))

      if (!sorted.length) {
        showError('Нет свободных слотов для копирования')
        return
      }

      const text = sorted.map((slot, index) => `${index + 1}. ${formatSlotForCopy(slot.starts_at)}`).join('\n')
      navigator.clipboard.writeText(text).then(() => showSuccess('Список скопирован'))
    }

    loadSlots()

    return {
      isLoading, isImporting, deletingId, slots, columns, importText, importResult,
      skipReasonLabel, formatSlotDateTime, doImport, removeSlot, copyList,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
